import { useEffect, useRef, useState } from "react";
import { Button, Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import useDelete from "../../../global/hooks/useDelete";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { FaAddressCard } from "react-icons/fa6";
import { termOptions, codetypeOptions } from "../../../utils/Common";
import AccountCreationModal from "../../../global/components/AccountCreationModal";
import OemIcon from "../../../assets/images/vectors/SideBarIcons/OemIcon";

const ADD_DEFAULT_VALUES = {
  CodeType: 0, // On Premise
  Term: 1, // 1 Month
};

export const LicenseCodeForm = ({
  type,
  isModalForm,
  onCancel,
  onCreated,
  defaultValues,
  lockCodeType = false,
}) => {
  const loggedInUser = JSON.parse(sessionStorage.getItem("userData"));
  const isSystemUser = [1, 2, 3].includes(loggedInUser?.role_value);
  const hideOemForOemAccount = [4, 5, 6].includes(loggedInUser?.role_value);
  const lockFieldsForOemEdit = !isSystemUser && type === "edit";
  const fieldsDisabled = type === "info" || lockFieldsForOemEdit;
  const [showModal, setShowModal] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [defaultData, setDefaultData] = useState({
    DealerNo: null,
    OemNo: null,
    CustomerNo: null,
  });

  const [form] = Form.useForm();
  const oemDefaultAppliedRef = useRef(false);
  const params = useParams();
  const id = isModalForm ? undefined : params.id;
  const navigate = useNavigate();

  const url = id
    ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/${id}/`
    : undefined;
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`;
  const postUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/${id}/`;
  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/`;

  const { data, loading, refetch } = useGet(url);
  const {
    data: optionsData,
    refetch: optionRefetch,
    loading: optionLoading,
  } = useGet(optionUrl);

  const {
    data: postData,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);
  const { data: putData, error: putError, putData: putAction } = usePut(putUrl);
  const {
    data: deletedData,
    error: deletedDataError,
    deleteMultiple,
  } = useDelete(deleteUrl);

  const modifiedItems = getModifiedResponse(optionsData, "items");
  const modifiedOems = getModifiedResponse(
    optionsData,
    "oem_license_enabled_oems",
  );

  const handleCreateNew = (modal) => {
    setModalType(modal);
    setIsOpenModal(true);
  };

  const onFinish = async (values) => {
    const toMoney = (v) =>
      v !== undefined && v !== null && v !== ""
        ? Number(parseFloat(v).toFixed(2))
        : v;

    const modifiedValue = {
      ...values,
      OemNo: hideOemForOemAccount
        ? (loggedInUser?.oem_no ?? null)
        : values.OemNo === "null"
          ? null
          : (values.OemNo ?? null),
      Price: toMoney(values.Price),
      OemPrice: toMoney(values.OemPrice),
      Code: parseInt(values.Code),
    };

    try {
      if (id) {
        await putAction(modifiedValue);
      } else {
        await postAction(modifiedValue);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  const onReset = () => {
    if (isModalForm) {
      form.resetFields();
      onCancel?.();
      return;
    }
    navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteMultiple({ ids: [Number(id)] });
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  // Prefill for Edit / Info
  useEffect(() => {
    try {
      form.resetFields();
      if (data?.data) {
        form.setFieldsValue({
          Code: data?.data?.Code,
          CodeName: data?.data?.CodeName || "",
          CodeDesc: data?.data?.CodeDesc || "",
          OemNo: data?.data?.OemNo ?? "null",
          CodeType: data?.data?.CodeType,
          Item: data?.data?.Item,
          Term: data?.data?.Term,
          Price: data?.data?.Price,
          OemPrice: data?.data?.OemPrice,
        });
      } else if (!id) {
        form.setFieldsValue({
          ...ADD_DEFAULT_VALUES,
          ...(defaultValues?.CodeType != null
            ? { CodeType: defaultValues.CodeType }
            : {}),
          ...(defaultValues?.OemNo != null
            ? { OemNo: defaultValues.OemNo }
            : {}),
        });
      }
    } catch (error) {
      console.log({ error });
      openErrorNotification("Error fetching data!");
    }
  }, [id, data, form, type]);

  // OEM default on Add, applied once so later re-renders don't overwrite user choices.
  useEffect(() => {
    if (id) {
      oemDefaultAppliedRef.current = false;
      return;
    }
    if (!oemDefaultAppliedRef.current && modifiedOems.length) {
      if (form.getFieldValue("OemNo") == null) {
        form.setFieldsValue({ OemNo: "null" });
      }
      oemDefaultAppliedRef.current = true;
    }
  }, [id, modifiedOems]);

  useEffect(() => {
    if (!isModalForm || defaultValues?.Item == null) return;
    form.setFieldsValue({ Item: defaultValues.Item });
  }, []);

  // After creating an OEM in the modal: reload the list and select it
  useEffect(() => {
    if (defaultData?.OemNo) {
      optionRefetch && optionRefetch();
      form.setFieldsValue({ OemNo: defaultData.OemNo });
    }
  }, [defaultData?.OemNo]);

  //toast for create
  useEffect(() => {
    if (Array.isArray(postError?.response?.data?.errors?.non_field_errors)) {
      postError.response.data.errors.non_field_errors.forEach((item) =>
        openErrorNotification(item),
      );
      return;
    }
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postError && postData) {
      openConfirmNotification("Success");
      if (isModalForm) {
        onCreated?.(postData?.data);
        onCancel?.();
        return;
      }
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [postError, postData]);

  //toast for update
  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putError && putData) {
      openConfirmNotification("Success");
      refetch();
      navigate("/licensecode");
    }
  }, [putData, putError]);

  //toast for delete
  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate("/licensecode");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  const breadcrumbItems = [
    { label: "License Code", href: "/licensecode" },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/licensecode/info/${id}`
          : `/licensecode/edit/${id}`
        : "/licensecode/add/",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => navigate(`/licensecode/edit/${id}`),
          },
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => setShowModal(true),
          },
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => navigate(-1),
          },
        ]
      : [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  const colProps = isModalForm
    ? { span: 24 }
    : { xs: 24, sm: 24, md: 12, lg: 12, xl: 12 };

  return (
    <div>
      {!isModalForm && (
        <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
          <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
            <CommonButton buttonsData={buttonsData} />
          </CommonBreadcrumb>
        </div>
      )}

      <div className="">
        <Form
          validateTrigger={"onChange"}
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          layout={"vertical"}
          autoComplete={"off"}
        >
          <div
            className={`bg-white overflow-hidden ${
              isModalForm ? "" : "border rounded-[4px]"
            }`}
          >
            {!isModalForm && (
              <div className="flex items-center bg-gray-200 p-2.5">
                <FaAddressCard className="text-[18px] text-black " />
                <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                  License Code
                </span>
              </div>
            )}
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col {...colProps}>
                  <CustomInput
                    label="Name"
                    name="CodeName"
                    disabled={fieldsDisabled}
                    autoComplete="off"
                    required
                    loading={loading}
                  />
                </Col>

                <Col {...colProps}>
                  <CustomInput
                    label="Description"
                    name="CodeDesc"
                    disabled={fieldsDisabled}
                    autoComplete="off"
                    loading={loading}
                  />
                </Col>

                {hideOemForOemAccount ? null : (
                  <Col {...colProps}>
                    <CustomSelect
                      label="OEM"
                      name="OemNo"
                      options={[
                        { label: "Generic", value: "null" },
                        ...modifiedOems,
                      ]}
                      placeholder="Please select"
                      disabled={fieldsDisabled}
                      // required
                      allowClear={false}
                      handleCreateNew={
                        isSystemUser && type !== "info" ? handleCreateNew : null
                      }
                      type={"OEM"}
                      loading={loading || optionLoading}
                    />
                  </Col>
                )}

                <Col {...colProps}>
                  <CustomSelect
                    label="Code Type"
                    name="CodeType"
                    options={codetypeOptions}
                    placeholder="Please select"
                    disabled={fieldsDisabled || isModalForm}
                    allowClear={false}
                    required
                    loading={loading}
                  />
                </Col>

                <Col {...colProps}>
                  <CustomSelect
                    label="Item"
                    name="Item"
                    options={modifiedItems}
                    placeholder="Please select"
                    disabled={fieldsDisabled}
                    required
                    loading={loading}
                  />
                </Col>

                <Col {...colProps}>
                  <CustomInput
                    label="Code"
                    name="Code"
                    disabled={fieldsDisabled}
                    type="number"
                    loading={loading}
                    required
                    integerOnly
                  />
                </Col>

                <Col {...colProps}>
                  <CustomSelect
                    label="Term"
                    name="Term"
                    options={termOptions}
                    placeholder="Please select"
                    disabled={fieldsDisabled}
                    allowClear={false}
                    required
                    loading={loading}
                  />
                </Col>

                <Col {...colProps}>
                  <CustomInput
                    label="Price"
                    name="Price"
                    type="number"
                    prefix={"$"}
                    style={{ height: "30px" }}
                    disabled={type === "info"}
                    required
                    loading={loading}
                  />
                </Col>

                {isSystemUser ? (
                  <Col {...colProps}>
                    <CustomInput
                      label="OEM Price"
                      name="OemPrice"
                      type="number"
                      prefix={"$"}
                      style={{ height: "30px" }}
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                ) : null}
              </Row>
            </div>
          </div>
        </Form>

        {isModalForm && (
          <div className="flex justify-center space-x-2 mt-2.5">
            <Button
              type="primary"
              className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
              onClick={() => form.submit()}
            >
              <CheckCircleOutlined className="!text-[14px] font-medium" />
              <span>Apply</span>
            </Button>
            <Button className="reset-button" onClick={onReset}>
              <CloseOutlined />
              <span>Cancel</span>
            </Button>
          </div>
        )}
      </div>

      <AccountCreationModal
        isOpenModal={isOpenModal}
        modalType={modalType}
        setIsOpenModal={setIsOpenModal}
        setDefaultData={setDefaultData}
        defaultData={defaultData}
        setModalType={setModalType}
        isModal={true}
        isModalForm={true}
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            {modalType === "OEM" ? (
              <OemIcon className="text-[15px] text-black ml-1" />
            ) : null}
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              {`Add ${modalType}`}
            </span>
          </div>
        }
      />

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </div>
  );
};
